import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Plus, Target, Upload } from "lucide-react";
import type { OutreachProspect } from "@lumika/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  PageHeader,
  PageLoading,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { ImportProspectsDialog } from "./ImportProspectsDialog";
import {
  EMPTY_PROSPECT,
  ProspectFormFields,
  ProspectFormSchema,
  toProspectPayload,
} from "./ProspectForm";
import { STAGE_ORDER, STAGE_LABELS } from "./outreach-ui";
import { OutreachBoard } from "./board/OutreachBoard";
import { ProspectPanel } from "./board/ProspectPanel";

const ALL = "__all__";
const BOARD_LIMIT = 200;

export function OutreachPage() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [stage, setStage] = useState<string>(ALL);
  const [owner, setOwner] = useState<string>(ALL);
  const [tag, setTag] = useState<string>(ALL);
  const [dueOnly, setDueOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [panelProspect, setPanelProspect] = useState<OutreachProspect | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const adminsQuery = useQuery({ queryKey: ["admins"], queryFn: adminApi.listAdmins });
  const admins = useMemo(() => adminsQuery.data ?? [], [adminsQuery.data]);

  // Fetch all prospects in one call (high limit); board groups them client-side.
  const listQuery = useQuery({
    queryKey: ["outreach", "prospects", { debounced, stage, owner, dueOnly, limit: BOARD_LIMIT }],
    queryFn: () =>
      adminApi.listOutreachProspects({
        search: debounced,
        stage: stage === ALL ? undefined : (stage as OutreachProspect["stage"]),
        owner: owner === ALL ? undefined : owner,
        due: dueOnly || undefined,
        limit: BOARD_LIMIT,
      }),
  });

  const prospects = listQuery.data?.items ?? [];

  // Tag options come from the full loaded set (before the tag filter), so the
  // dropdown stays complete regardless of which tag is selected. The tag filter
  // itself runs client-side — the board already loads the whole pipeline.
  const availableTags = useMemo(
    () => Array.from(new Set(prospects.flatMap((p) => p.tags))).sort(),
    [prospects],
  );
  const visibleProspects = tag === ALL ? prospects : prospects.filter((p) => p.tags.includes(tag));
  const isFiltered = !!(debounced || stage !== ALL || owner !== ALL || tag !== ALL || dueOnly);

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Pipeline"
        title="Outreach"
        description="Our own pipeline of prospective dealerships — who they are, where they stand, and when to follow up."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImporting(true)}>
              <Upload className="h-4 w-4" />
              Import
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add prospect
            </Button>
          </div>
        }
      />

      {/* Toolbar: search + owner + due filter */}
      <div className="flex shrink-0 flex-col gap-3 md:flex-row md:items-center">
        <SearchInput
          containerClassName="flex-1"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search company, contact, phone, or email"
          aria-label="Search prospects"
        />
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="md:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All stages</SelectItem>
            {STAGE_ORDER.map((s) => (
              <SelectItem key={s} value={s}>
                {STAGE_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={owner} onValueChange={setOwner}>
          <SelectTrigger className="md:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All owners</SelectItem>
            {admins.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.displayName ?? a.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {availableTags.length > 0 && (
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger className="md:w-44" aria-label="Filter by tag">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All tags</SelectItem>
              {availableTags.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button
          variant={dueOnly ? "default" : "outline"}
          onClick={() => setDueOnly((v) => !v)}
          aria-pressed={dueOnly}
        >
          <Bell className="h-4 w-4" />
          Due
        </Button>
      </div>

      {/* Board — fills the remaining height; each column scrolls internally */}
      <div className="min-h-0 flex-1">
        {listQuery.isLoading ? (
          <PageLoading message="Loading prospects…" />
        ) : visibleProspects.length === 0 && isFiltered ? (
          <EmptyState
            icon={<Target className="h-5 w-5" />}
            title="No matching prospects"
            description="Adjust the filters to see more."
          />
        ) : visibleProspects.length === 0 ? (
          <EmptyState
            icon={<Target className="h-5 w-5" />}
            title="No prospects yet"
            description="Add your first dealer prospect, or import a list."
            action={
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                Add prospect
              </Button>
            }
          />
        ) : (
          <OutreachBoard
            prospects={visibleProspects}
            admins={admins}
            onCardClick={setPanelProspect}
          />
        )}
      </div>

      {/* Slide-over panel */}
      <ProspectPanel
        prospect={panelProspect}
        admins={admins}
        onClose={() => setPanelProspect(null)}
      />

      {creating && <CreateProspectDialog admins={admins} onClose={() => setCreating(false)} />}
      {importing && <ImportProspectsDialog onClose={() => setImporting(false)} />}
    </div>
  );
}

function CreateProspectDialog({
  admins,
  onClose,
}: {
  admins: Parameters<typeof ProspectFormFields>[0]["admins"];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { values, errors, setField, validate } = useZodForm(ProspectFormSchema, EMPTY_PROSPECT);

  const createMutation = useMutation({
    mutationFn: () => adminApi.createOutreachProspect(toProspectPayload(values)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["outreach"] });
      toast.success("Prospect added");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    if (!validate().ok) return;
    createMutation.mutate();
  }

  const isDirty = JSON.stringify(values) !== JSON.stringify(EMPTY_PROSPECT);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>Add prospect</DialogTitle>
          <DialogDescription>A prospective dealership for our pipeline.</DialogDescription>
        </DialogHeader>
        <div className="-mx-2 -my-1.5 overflow-y-auto px-2 py-1.5">
          <ProspectFormFields
            values={values}
            errors={errors}
            setField={setField}
            admins={admins}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={createMutation.isPending}>
            {createMutation.isPending ? "Saving…" : "Add prospect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
